import React, { Component } from 'react';
import '@modernhealth/vital-components/dist/style.css';
import './styles/_font.scss';
import CircularProgress from '@material-ui/core/CircularProgress';
import CssBaseline from '@material-ui/core/CssBaseline';
import { MuiThemeProvider } from '@material-ui/core/styles';
import * as Sentry from '@sentry/browser';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { pick } from 'lodash';
import { SnackbarProvider } from 'notistack';
import { ApolloProvider } from 'react-apollo';
import gql from 'graphql-tag';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { AnalyticsProvider } from '@modernhealth/web-analytics';

import { chatClient } from 'src/config/Apollo';
import { analyticsClient } from 'src/services/analytics';

import { AppContext, ContextDefaults } from './contexts/AppContext';
import { ChatProvider } from './contexts/chat-context';
import { parseJWT } from './helpers/jwtParser';
import { UserRole } from './models/User';
import { Routes } from './routing/Routes';
import { store } from './config/Store';
import { getPresenceState } from './selectors/presenceSelectors';
import auth from './services/Auth';
import { MobileDeviceWarning } from './components/MobileDeviceWarning';
import NavBar from './components/NavBar';
import RouteListener from './components/RouteListener';
import SnackbarNotifier from './components/SnackbarNotifier';
import { UpdateTimezoneModal } from './components/UpdateTimezoneModal';
import { theme } from './themes';
import './App.css';
import { FeatureFlagsManager } from './components/FeatureFlagsManager';

const DEFAULT_TITLE = 'Modern Health Provider Dashboard';
const NEW_MESSAGE_TITLE_1 = 'Modern Health Provider Dashboard- You have a new message';
const NEW_MESSAGE_TITLE_2 = 'New Message...';

const EMOJI_ATTRIBUTION = `
Emoji on this site are provided by Twemoji (https://twemoji.twitter.com/) under the CC-BY 4.0 license.
`;

export const APP_STATUS_QUERY = gql`
  query getAppStaus {
    ready @client
  }
`;

export class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      ready: false,
      initializeApp: this.initializeApp,
    };
  }

  async componentDidMount() {
    // eslint-disable-next-line no-console
    console.log(EMOJI_ATTRIBUTION);

    const authData = await auth.isAuthenticated();
    const isAuthenticated = !!authData;

    if (isAuthenticated) {
      await this.initializeApp();
    } else {
      this.setState({ ready: true });
    }
  }

  // On every new message, if the window is not focused, change the title every 2 seconds in an annoying fashion
  startTitleChanger = () => {
    window.setInterval(() => {
      // When the title becomes focused again, go back to the normal title
      if (!document.hidden) {
        document.title = DEFAULT_TITLE;
        return;
      }

      // Swap between two different "New Message" titles
      if (document.title === NEW_MESSAGE_TITLE_1) {
        document.title = NEW_MESSAGE_TITLE_2;
      } else if (document.title === NEW_MESSAGE_TITLE_2) {
        document.title = NEW_MESSAGE_TITLE_1;
      }
    }, 2000);
  };

  initializeApp = async () => {
    chatClient.cache.writeQuery({
      query: APP_STATUS_QUERY,
      data: {
        ready: false,
      },
    });
    const claims = parseJWT();

    if (
      !claims ||
      (!claims.rol.includes(UserRole.Coach) && !claims.rol.includes(UserRole.GroupPractice))
    ) {
      Sentry.captureMessage('User is not a coach! Redirecting to member webapp');

      auth.clearAuthData();

      return window.location.replace(process.env.REACT_APP_DEFAULT_WEBAPP_FRONTEND_URL);
    }

    await store.dispatch.user.fetchCurrentUser();
    const isInvoicingTherapist = store.getState().user.currentUser.providerInvoicingOnly;

    await store.dispatch.partners.fetchPartners();

    // Set app wide state ready to true, remove app wide loader
    this.setState({ ready: true });

    // Group practices and invoicing-only therapists don't have chat, so only subscribe to chat-related things if the user is a regular in-app provider.
    if (!claims.rol.includes(UserRole.GroupPractice) && !isInvoicingTherapist) {
      await store.dispatch.chat.fetchFirstPageConversations();
      await store.dispatch.resources.fetchResources();

      await store.dispatch.presence.startOnProviderDashboardSubscription();
      // Start a subscription with the last stored state.
      await store.dispatch.presence.updatePresence(getPresenceState(store.getState()));
      // Start polling for unread chat messages
      store.dispatch.chat.startPollingUnreads();

      this.startTitleChanger();
    }

    chatClient.cache.writeQuery({
      query: APP_STATUS_QUERY,
      data: {
        ready: true,
      },
    });
  };

  render() {
    return (
      <LDProvider
        clientSideID={process.env.REACT_APP_DEFAULT_LAUNCH_DARKLY_CLIENT_ID || ''}
        user={{ anonymous: true }}
        options={{ allAttributesPrivate: true, sendEvents: true }}
      >
        {this.state.ready ? (
          <Router>
            <Provider store={store}>
              <RouteListener>
                <ApolloProvider client={store.getState().network.client}>
                  <AnalyticsProvider analyticsClient={analyticsClient}>
                    <AppContext.Provider value={pick(this.state, Object.keys(ContextDefaults))}>
                      <FeatureFlagsManager />
                      <ChatProvider chatClient={chatClient}>
                        <MuiThemeProvider theme={theme}>
                          <SnackbarProvider maxSnack={3}>
                            <CssBaseline />
                            <UpdateTimezoneModal />
                            <MobileDeviceWarning />
                            <NavBar />
                            <Routes />
                            <SnackbarNotifier />
                          </SnackbarProvider>
                        </MuiThemeProvider>
                      </ChatProvider>
                    </AppContext.Provider>
                  </AnalyticsProvider>
                </ApolloProvider>
              </RouteListener>
            </Provider>
          </Router>
        ) : (
          <div data-testid="AppTestID.Spinner" className="AppLoading">
            <CircularProgress size={100} />
          </div>
        )}
      </LDProvider>
    );
  }
}
